import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import LoadDataFile from "../../Common/LoadDataFile";
import { showSnackbar } from "../../../../service/snackbar.service";
import { uniqBy } from "lodash";
import { ChamberReportFields, IChamberTrial } from "./columns";
import ChamberReport from "./ChamberReport";

enum ChamberReportGroupBy {
  Date = "Date",
  Cow = "Cow",
}
const parseTrialRow = (
  row: any[],
  headerRow: any[],
  dateColumnIndex: number,
  chamberColumnIndex: number
) => {
  const fieldsObject: any = {};
  Object.keys(ChamberReportFields).forEach((key) => {
    const columnName = (ChamberReportFields as any)[key];
    const columnIndex = (headerRow || []).findIndex(
      (cell) =>
        cell?.toLowerCase?.()?.trim() === columnName.toLowerCase?.().trim()
    );
    if (columnIndex > -1) {
      fieldsObject[key] = row[columnIndex];
    }
  });
  const parsedTrial: IChamberTrial = {
    date: row[dateColumnIndex],
    chamberName: row[chamberColumnIndex],
    fields: fieldsObject,
  };
  return parsedTrial;
};
export default function ChamberReportDialog({
  onClose,
}: {
  onClose: () => void;
}) {
  const [fileName, setFileName] = React.useState<string>("");
  const [trialsByDates, setTrialsByDates] = React.useState<
    { group: string; trials: IChamberTrial[] }[]
  >([]);
  const [trialsByCows, setTrialsByCows] = React.useState<
    { group: string; trials: IChamberTrial[] }[]
  >([]);
  const [openedTrials, setOpenedTrials] = React.useState<IChamberTrial[]>();
  const [groupBy, setGroupBy] = React.useState<ChamberReportGroupBy>(
    ChamberReportGroupBy.Date
  );

  const handleUploadFile = (parsedData: any[][], fileName?: string) => {
    setFileName(fileName || "");
    const maxRowLength = Math.max(
      ...parsedData.map((row) => row.filter((cell) => cell).length)
    );
    const headerRow = parsedData.find(
      (row) => row.filter((cell) => cell).length === maxRowLength
    );
    if (!headerRow) {
      showSnackbar("danger", "Parse Error", "Header row not found");
      return;
    }
    const headerRowIndex = parsedData.findIndex(
      (row) => row.filter((cell) => cell).length === maxRowLength
    );
    const dateColumnIndex = headerRow.findIndex((cell) =>
      cell?.toLowerCase?.()?.endsWith("date")
    );
    const cowColumnIndex = headerRow?.findIndex((cell) =>
      cell?.toLowerCase?.()?.startsWith("cow")
    );
    if (cowColumnIndex < 0) {
      showSnackbar("danger", "Parse Error", "Cow column not found");
      return;
    }
    if (dateColumnIndex < 0) {
      showSnackbar("danger", "Parse Error", "Date column not found");
      return;
    }
    const chamberColumnIndex = headerRow?.findIndex((cell) =>
      cell?.toLowerCase?.()?.startsWith("chamber")
    );
    if (chamberColumnIndex < 0) {
      showSnackbar("danger", "Parse Error", "Chamber column not found");
      return;
    }
    const allDates = parsedData
      .slice(headerRowIndex + 1)
      .map((row) => row[dateColumnIndex])
      .filter((v) => v);
    const allUniqueDates = uniqBy(allDates, (v) => v);
    const allCows = parsedData
      .slice(headerRowIndex + 1)
      .map((row) => row[cowColumnIndex])
      .filter((v) => v);
    const allUniqueCows = uniqBy(allCows, (v) => v);
    const tempTrialsByDates = allUniqueDates.map((date) => {
      const trialsThisDate = parsedData
        .slice(headerRowIndex + 1)
        .filter((row) => row[dateColumnIndex] === date);
      const parsedTrials = trialsThisDate.map((row) => {
        return parseTrialRow(
          row,
          headerRow,
          dateColumnIndex,
          chamberColumnIndex
        );
      });
      return {
        group: date,
        trials: parsedTrials,
      };
    });

    const tempTrialsByCows = allUniqueCows.map((cow) => {
      const trialsThisDate = parsedData
        .slice(headerRowIndex + 1)
        .filter((row) => row[cowColumnIndex] === cow);
      const parsedTrials = trialsThisDate.map((row) => {
        return parseTrialRow(
          row,
          headerRow,
          dateColumnIndex,
          chamberColumnIndex
        );
      });
      return {
        group: cow,
        trials: parsedTrials,
      };
    });
    setTrialsByCows(tempTrialsByCows);
    setTrialsByDates(tempTrialsByDates);
  };
  return (
    <Dialog
      open
      onClose={() => {
        setFileName("");
        onClose();
      }}
      maxWidth={false}
      scroll="paper"
    >
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center" }}>
          Chamber Report
          <div style={{ flex: 1 }} />
          <RadioGroup
            row
            onChange={(e) => setGroupBy(e.target.value as ChamberReportGroupBy)}
            sx={{ ml: 4 }}
          >
            <FormControlLabel
              value={ChamberReportGroupBy.Date}
              control={
                <Radio checked={groupBy === ChamberReportGroupBy.Date} />
              }
              label="By Date"
            />
            <FormControlLabel
              value={ChamberReportGroupBy.Cow}
              control={<Radio checked={groupBy === ChamberReportGroupBy.Cow} />}
              label="By Cow"
            />
          </RadioGroup>
        </div>
      </DialogTitle>
      <DialogContent>
        {!fileName ? (
          <LoadDataFile onParseData={handleUploadFile} />
        ) : (
          <Box
            sx={{
              "& td": {
                padding: "4px 8px",
              },
              "& th": {
                padding: "4px 8px",
              },
            }}
          >
            {(groupBy === ChamberReportGroupBy.Date
              ? trialsByDates
              : trialsByCows
            )?.map((trialsByDate) => (
              <Paper
                key={trialsByDate.group}
                sx={{ border: "solid 1px #ddd", px: 2, py: 1, mb: 2 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6" color="warning.main">
                    {trialsByDate.group}
                  </Typography>

                  <Button
                    onClick={() => setOpenedTrials(trialsByDate.trials)}
                    size="small"
                    variant="outlined"
                    startIcon={<SummarizeIcon />}
                  >
                    Get Report
                  </Button>
                </div>
                <Box>
                  <TableContainer sx={{ borderTop: "solid 1px #ddd", mt: 1 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {groupBy === ChamberReportGroupBy.Date ? null : (
                            <TableCell>Date</TableCell>
                          )}
                          <TableCell>Chamber</TableCell>
                          <TableCell>Treatment</TableCell>
                          {groupBy === ChamberReportGroupBy.Cow ? null : (
                            <TableCell>Cow</TableCell>
                          )}
                          <TableCell>Weight (Kg)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {trialsByDate.trials.map((trial) => (
                          <TableRow
                            key={`${trial.date}-${trial.chamberName}-${trial.fields?.cow}`}
                          >
                            {groupBy === ChamberReportGroupBy.Date ? null : (
                              <TableCell>{trial.date}</TableCell>
                            )}
                            <TableCell>{trial.chamberName}</TableCell>
                            <TableCell>{trial.fields?.treatment}</TableCell>
                            {groupBy === ChamberReportGroupBy.Cow ? null : (
                              <TableCell>{trial.fields?.cow}</TableCell>
                            )}
                            <TableCell>
                              {trial.fields?.animalWeightKg
                                ? `${trial.fields?.animalWeightKg} kg`
                                : ""}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      {openedTrials?.length ? (
        <ChamberReport
          byCow={groupBy === ChamberReportGroupBy.Cow}
          byDate={groupBy === ChamberReportGroupBy.Date}
          trials={openedTrials}
          onClose={() => setOpenedTrials(undefined)}
        />
      ) : null}
    </Dialog>
  );
}
